import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { FileList } from "../../ui/FileList.tsx";

interface Collection {
  files?: any[];
  [key: string]: any;
}

interface FileExplorerProps {
  collections: { [key: string]: Collection };
  currentPath: string[];
  isLoading: boolean;
  isDarkMode: boolean;
  viewMode: "list" | "grid";
  onFolderClick: (folderName: string) => void;
  onDeleteFile: (fileName: string, collectionName: string) => void;
  onDownloadFile?: (filename: string) => void;
  truncateFileName: (fileName: string, maxLength?: number) => string;
}

const FileExplorer = ({
  collections,
  currentPath,
  isLoading,
  isDarkMode,
  viewMode,
  onFolderClick,
  onDeleteFile,
  onDownloadFile,
  truncateFileName
}: FileExplorerProps) => {
  const [textConfig, setTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  let currentDirectory = collections;
  for (const pathPart of currentPath) {
    if (currentDirectory?.[pathPart]) {
      currentDirectory = currentDirectory[pathPart];
    } else {
      break;
    }
  }

  const currentItems = [
    ...Object.entries(currentDirectory || {})
      .filter(([key]) => key !== "files")
      .map(([name]) => ({
        name,
        type: "folder" as const,
        onClick: () => onFolderClick(name),
      })),
    ...(currentDirectory?.files?.map((file: any) => {
      
      const fileName = decodeURIComponent(file.FILE_NAME || "")
        .split("/")
        .pop() || "";

      return {
        name: fileName,
        displayName: truncateFileName(fileName),
        type: "file" as const,
        hash: file.FILE_HASH,
        collection: file.COLLECTION_NAME,
        onDelete: () => onDeleteFile(file.FILE_NAME, file.COLLECTION_NAME),
        onDownload: onDownloadFile ? () => onDownloadFile(file.FILE_HASH) : undefined,
      };
    }) || []),
  ];

  return (
    <Box
      sx={{
        height: "200px",
        border: isDarkMode ? "1px solid #2D2D2D" : "1px solid #e5e7eb",
        borderRadius: 2,
        p: 2,
        mb: 2,
        backgroundColor: isDarkMode ? "#18181b" : "#ffffff",
        overflowY: "auto",
      }}
    >
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <Typography color={isDarkMode ? "white" : "black"}>
            {textConfig.FILE_EXPLORER_LOADING}
          </Typography>
        </Box>
      ) : currentItems.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <Typography 
            color={isDarkMode ? "#9CA3AF" : "#6B7280"}
            variant="body2"
          >
            {textConfig.FILE_EXPLORER_EMPTY_MESSAGE}
          </Typography>
        </Box>
      ) : (
        <FileList items={currentItems} viewMode={viewMode} />
      )}
    </Box>
  );
};

export default FileExplorer;